import anuncioNavegacaoService from '@/services/globals/anuncio-navegacao.service';

/**
 * Liga o erro devolvido pelo SERVIDOR ao campo que o causou.
 *
 * O problema que isto resolve (WCAG 3.3.1 — Identificação de Erro, nível A)
 * ----------------------------------------------------------------------------
 * O `setupInterceptors.ts` centraliza 400/403/404/500 num aviso flutuante. Centralizar é
 * a decisão certa — evita repetir tratamento de erro em cada chamada — mas o aviso não
 * tem como saber a que campo o erro se refere, e nada no formulário mudava.
 *
 * O resultado prático: a pessoa ouve "os dados enviados são inválidos" e não tem como
 * descobrir qual dos trinta campos está errado. Quem usa lupa não encontra a borda
 * vermelha (não havia borda); quem usa leitor de tela não tinha nem cor nem texto.
 *
 * Por que dá para fazer isto de forma genérica: as duas APIs devolvem
 * `ValidationProblemDetails` do ASP.NET Core, que é um mapa `{ campo: [mensagens] }`. O
 * nome do campo no mapa é o nome da propriedade do modelo, e o `vee-validate` (usado em
 * todo formulário do projeto) já emite `name="<propriedade>"` no input. Isso é o bastante
 * para achar o campo no DOM sem que cada tela precise participar.
 *
 * Por que manipular o DOM aqui, e não por estado do Vue: o interceptor não tem acesso ao
 * componente que fez a chamada. A alternativa seria propagar o erro por store até cada
 * formulário, o que exigiria alterar dezenas de telas para resolver o mesmo problema.
 * Aqui o efeito é aditivo e degrada em silêncio: se nenhum campo casar, nada acontece e o
 * aviso flutuante continua sendo o único retorno, como era antes.
 */

/** Marca deixada nos elementos que este serviço alterou, para saber o que limpar depois. */
const ATRIBUTO_MARCA = 'data-erro-servidor';

/**
 * Extrai o mapa `{ campo: mensagem }` de uma resposta de erro.
 *
 * Cobre só o `ValidationProblemDetails` (`data.errors`), que é o único formato das duas
 * APIs que identifica O CAMPO. `erros`/`Erros`/`mensagem` são mensagens de tela inteira e
 * continuam indo apenas para o aviso flutuante — associá-las a um campo qualquer seria
 * apontar o culpado errado, o que é pior que não apontar nenhum.
 */
export function extrairErrosPorCampo(err: any): Record<string, string> {
  const errors = err?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return {};

  const mapa: Record<string, string> = {};
  for (const [campo, mensagens] of Object.entries(errors as Record<string, unknown>)) {
    if (!campo) continue;
    const texto = Array.isArray(mensagens) ? mensagens.join(' ') : String(mensagens ?? '');
    if (texto.trim()) mapa[campo] = texto.trim();
  }
  return mapa;
}

/**
 * Remove as marcações da tentativa anterior.
 *
 * Sem isto, corrigir um campo e reenviar deixaria a marca antiga: o leitor de tela
 * continuaria anunciando "inválido" num campo já corrigido, que é uma informação falsa —
 * e mais confusa que a ausência de informação.
 */
export function limparErrosDeCampo(): void {
  document.querySelectorAll<HTMLElement>(`[${ATRIBUTO_MARCA}]`).forEach((elemento) => {
    if (elemento.tagName === 'P' || elemento.tagName === 'SPAN') {
      // Elemento de mensagem criado por este serviço: sai inteiro.
      elemento.remove();
      return;
    }
    elemento.removeAttribute('aria-invalid');
    elemento.removeAttribute(ATRIBUTO_MARCA);

    const descricoes = (elemento.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && !id.startsWith('erro-servidor-'));
    if (descricoes.length) elemento.setAttribute('aria-describedby', descricoes.join(' '));
    else elemento.removeAttribute('aria-describedby');
  });
}

/**
 * Acha o campo no DOM pelo nome vindo da API.
 *
 * A comparação é sem diferenciar maiúsculas porque o servidor devolve a propriedade em
 * PascalCase (`NomeCompleto`) e o template escreve em camelCase (`nomeCompleto`).
 */
function acharCampo(nome: string): HTMLElement | null {
  const candidatos = Array.from(
    document.querySelectorAll<HTMLElement>('input[name], select[name], textarea[name]'),
  );
  const alvo = nome.toLowerCase();
  return (
    candidatos.find((el) => (el.getAttribute('name') || '').toLowerCase() === alvo) ?? null
  );
}

/**
 * Marca os campos com erro e leva o foco ao primeiro deles.
 *
 * Devolve quantos campos foram efetivamente encontrados — o interceptor usa isso para
 * decidir se ainda vale mostrar o aviso flutuante genérico.
 */
export function aplicarErrosDeCampo(err: any): number {
  const mapa = extrairErrosPorCampo(err);
  if (!Object.keys(mapa).length) return 0;

  limparErrosDeCampo();

  let primeiro: HTMLElement | null = null;
  let aplicados = 0;

  Object.entries(mapa).forEach(([nome, mensagem], indice) => {
    const campo = acharCampo(nome);
    if (!campo) return;

    const idMensagem = `erro-servidor-${indice}`;

    /*
     * `role="alert"` de propósito: é o único papel de região viva que os leitores de tela
     * anunciam ao ser INSERIDO no DOM, que é exatamente o caso aqui — a mensagem nasce
     * junto com o elemento, em resposta a uma ação do usuário.
     */
    const aviso = document.createElement('p');
    aviso.id = idMensagem;
    aviso.className = 'invalid-feedback d-block';
    aviso.setAttribute('role', 'alert');
    aviso.setAttribute(ATRIBUTO_MARCA, '');
    aviso.textContent = mensagem;
    campo.parentElement?.insertBefore(aviso, campo.nextSibling);

    campo.setAttribute('aria-invalid', 'true');
    campo.setAttribute(ATRIBUTO_MARCA, '');

    // Preserva um `aria-describedby` que a tela já tenha (texto de apoio do campo).
    const descricoes = (campo.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    descricoes.push(idMensagem);
    campo.setAttribute('aria-describedby', descricoes.join(' '));

    if (!primeiro) primeiro = campo;
    aplicados += 1;
  });

  if (primeiro) {
    /*
     * Levar o foco ao primeiro campo com erro é o que transforma a mensagem em algo
     * acionável: sem isso a pessoa sabe que há um erro e não sabe onde. `preventScroll`
     * fica FALSO (padrão) porque rolar até o campo é justamente o que se quer.
     */
    (primeiro as HTMLElement).focus();
    anuncioNavegacaoService.anunciar(
      aplicados === 1
        ? 'Um campo precisa de correção. O foco foi movido para ele.'
        : `${aplicados} campos precisam de correção. O foco foi movido para o primeiro.`,
    );
  }

  return aplicados;
}

export default { extrairErrosPorCampo, aplicarErrosDeCampo, limparErrosDeCampo };
